import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-xl text-slate-900">Nexus Leads</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">Terms of Service</h1>
        <p className="text-slate-500 mb-8">Last updated: January 2026</p>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Acceptance of Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing or using Nexus Leads ("Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our Service. We reserve the right to 
              modify these terms at any time, and your continued use of the Service constitutes acceptance 
              of any changes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Description of Service</h2>
            <p className="text-slate-600 leading-relaxed">
              Nexus Leads is a B2B lead generation and sales intelligence platform that provides access to 
              business lead data, AI-powered analysis, email generation, and CRM functionality. The Service 
              is designed for sales professionals, marketing agencies, and businesses seeking to identify 
              and engage potential customers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">3. User Accounts</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              To access certain features of the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Not share your account with others or create multiple accounts</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">4. Subscription and Payment</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              Our Service offers various subscription plans with different features and limitations:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>All plans are paid subscriptions billed in advance on a monthly basis</li>
              <li>Payment is required at signup — we do not offer free or trial subscriptions</li>
              <li>All fees are non-refundable unless otherwise stated</li>
              <li>We reserve the right to modify pricing with 30 days notice</li>
              <li>Failure to pay may result in suspension or termination of access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">5. Acceptable Use</h2>
            <p className="text-slate-600 leading-relaxed mb-3">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Violate any applicable laws or regulations</li>
              <li>Send spam or unsolicited communications</li>
              <li>Harass, abuse, or harm others</li>
              <li>Scrape, copy, or redistribute our data without permission</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use automated tools to access the Service beyond normal use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed">
              The Service, including its original content, features, and functionality, is owned by 
              Nexus Leads and is protected by international copyright, trademark, and other intellectual 
              property laws. You may not copy, modify, distribute, or create derivative works without 
              our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Data and Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              Your use of the Service is also governed by our Privacy Policy. By using the Service, 
              you consent to the collection and use of information as described in our Privacy Policy. 
              You are responsible for ensuring your use of lead data complies with applicable privacy 
              laws, including CAN-SPAM, GDPR, and CCPA where applicable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Disclaimer of Warranties</h2>
            <p className="text-slate-600 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THE 
              ACCURACY, COMPLETENESS, OR RELIABILITY OF ANY LEAD DATA OR AI-GENERATED CONTENT. WE 
              DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY AND FITNESS FOR 
              A PARTICULAR PURPOSE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed">
              IN NO EVENT SHALL NEXUS LEADS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR 
              CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL 
              NOT EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE TWELVE MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Termination</h2>
            <p className="text-slate-600 leading-relaxed">
              We may terminate or suspend your account and access to the Service immediately, without 
              prior notice, for conduct that we believe violates these Terms or is harmful to other 
              users, us, or third parties. Upon termination, your right to use the Service will 
              immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Contact Information</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at{' '}
              <a href="mailto:support@nexusleads.us" className="text-indigo-600 hover:underline">
                support@nexusleads.us
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-900 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 text-sm">
            © 2026 Nexus Leads. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
